import {
  Button,
  FormRoot,
  Modal,
  RHF,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
  zodResolver,
} from "@devhop/ui";
import { XIcon } from "lucide-react";
import { useEffect } from "react";
import type { Resolver } from "react-hook-form";
import { z } from "zod";
import type { CustomerDetailDTO, MemberTierDTO } from "../api/client";
import { useMemberTiersQuery, useUpdateCustomer } from "../api/queries";

const CustomerEditFormSchema = z.object({
  memberTierId: z.string().nullable(),
});

type CustomerEditFormValues = z.infer<typeof CustomerEditFormSchema>;

type CustomerEditDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: CustomerDetailDTO;
};

export function CustomerEditDialog({
  open,
  onOpenChange,
  customer,
}: CustomerEditDialogProps) {
  const updateCustomer = useUpdateCustomer();
  const tiers = useMemberTiersQuery({ limit: 100 });
  const tierOptions = tiers.data?.data ?? [];

  const methods = RHF.useForm<CustomerEditFormValues>({
    resolver: zodResolver(
      CustomerEditFormSchema,
    ) as Resolver<CustomerEditFormValues>,
    defaultValues: {
      memberTierId: customer.memberTierId ?? null,
    },
  });

  useEffect(() => {
    if (open) {
      methods.reset({
        memberTierId: customer.memberTierId ?? null,
      });
    }
  }, [open, customer.memberTierId]);

  const onSubmit = async (vals: CustomerEditFormValues) => {
    const memberTierId = vals.memberTierId === "" ? null : vals.memberTierId;
    await toast.promise(
      updateCustomer.mutateAsync({ id: customer.id, data: { memberTierId } }),
      {
        loading: "ກຳລັງບັນທຶກ...",
        success: "ບັນທຶກສຳເລັດ",
        error: "ບັນທຶກລົ້ມເຫຼວ",
      },
    );
    onOpenChange(false);
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} showCloseButton={false}>
      <FormRoot methods={methods} onSubmit={onSubmit}>
        <div className="space-y-5">
          <div className="flex items-start justify-between border-b pb-4">
            <h3 className="font-semibold text-lg">ແກ້ໄຂລະດັບສະມາຊິກ</h3>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="ປິດ"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>

          <RHF.Controller
            name="memberTierId"
            control={methods.control}
            render={({ field }) => (
              <div className="space-y-2">
                <label className="font-medium text-sm">ລະດັບສະມາຊິກ</label>
                <Select
                  value={field.value ?? "none"}
                  onValueChange={(v) => field.onChange(v === "none" ? null : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="ເລືອກລະດັບສະມາຊິກ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— ບໍ່ກຳນົດ —</SelectItem>
                    {tierOptions.map((t: MemberTierDTO) => {
                      const n = t.name as {
                        lo?: string;
                        th?: string;
                        en?: string;
                      };
                      const label = n?.lo ?? n?.th ?? n?.en ?? t.id;
                      return (
                        <SelectItem key={t.id} value={t.id}>
                          {label}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}
          />

          <div className="flex justify-end gap-2 border-t pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateCustomer.isPending}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={updateCustomer.isPending}>
              {updateCustomer.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
            </Button>
          </div>
        </div>
      </FormRoot>
    </Modal>
  );
}
